import React, { useState, useRef, useEffect, useMemo } from "react";
import * as P from "./styles";
import { formatTime } from "utils/formatTime";
import SparkleIcon from "components/SpeakerEmotionBadge/SparkleIcon";
import { formatSpeakerName } from "utils/formatSpeaker";
import SpeakerEmotionBadge from "components/SpeakerEmotionBadge";

export interface MarkerData {
  start: number;
  end: number;
  type: "loudness" | "pitch" | "emotion" | "speed" | "highlight" | "disabled";
  text?: string;
  speaker?: string;
}

interface HighlightPlayerProps {
  src: string;
  duration: number;
  markers: MarkerData[];
  activeMarkerType: string | null;
  isHighlightVisible: boolean;
}

const HighlightPlayer: React.FC<HighlightPlayerProps> = ({
  src,
  duration,
  markers,
  activeMarkerType,
  isHighlightVisible,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<MarkerData | null>(null);

  const timelineWidth = useMemo(() => {
    const minWidth = 1000;
    const calculatedWidth = duration * 5;
    return Math.max(minWidth, calculatedWidth);
  }, [duration]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const animate = () => {
      setCurrentTime(audio.currentTime);
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }

    const handleEnded = () => {
      setIsPlaying(false);
    };
    audio.addEventListener("ended", handleEnded);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      audio.removeEventListener("ended", handleEnded);
    };
  }, [isPlaying]);

  const getMarkerTypeFromLabel = (
    label: string
  ): MarkerData["type"] | "all" => {
    switch (label) {
      case "감정":
        return "emotion";
      case "음량":
        return "loudness";
      case "속도":
        return "speed";
      case "톤":
        return "pitch";
      default:
        return "all";
    }
  };

  const processedMarkers = useMemo(() => {
    const targetType = activeMarkerType
      ? getMarkerTypeFromLabel(activeMarkerType)
      : "all";

    return markers
      .filter((marker) => {
        if (marker.type === "highlight") {
          return isHighlightVisible;
        }
        if (targetType === "all") {
          return true;
        }
        return marker.type === targetType;
      })
      .sort((a, b) => a.start - b.start);
  }, [markers, activeMarkerType, isHighlightVisible]);

  const highlightMarkers = useMemo(
    () => processedMarkers.filter((m) => m.type === "highlight"),
    [processedMarkers]
  );

  const pointMarkers = useMemo(
    () => processedMarkers.filter((m) => m.type !== "highlight"),
    [processedMarkers]
  );

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const timeline = timelineRef.current;
    const audio = audioRef.current;
    if (!timeline || !audio) return;

    const rect = timeline.getBoundingClientRect();
    const clickPosition = e.clientX - rect.left;
    const seekTime = (clickPosition / timelineWidth) * duration;

    audio.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleMarkerClick = (
    e: React.MouseEvent<HTMLDivElement>,
    marker: MarkerData
  ) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = marker.start;
    setCurrentTime(marker.start);
    setSelectedMarker(marker);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const getMarkerTypeText = (
    type: "loudness" | "pitch" | "emotion" | "speed" | "highlight" | "disabled"
  ) => {
    switch (type) {
      case "emotion":
        return "감정";
      case "loudness":
        return "음량";
      case "speed":
        return "속도";
      case "pitch":
        return "톤";
      case "highlight":
        return "하이라이트";
      case "disabled":
        return "";
      default:
        return "";
    }
  };

  return (
    <P.PlayerContainer>
      <audio ref={audioRef} src={src} preload="metadata" />
      <P.TimelineControlWrapper>
        <P.ScrollableContainer ref={scrollContainerRef}>
          <P.TimelineContainer
            ref={timelineRef}
            onClick={handleTimelineClick}
            style={{ width: `${timelineWidth}px` }}
          >
            <P.TimelineBar />
            {highlightMarkers.map((marker, index) => {
              const left = (marker.start / duration) * 100;
              const width = ((marker.end - marker.start) / duration) * 100;
              return (
                <P.HighlightBar
                  key={`highlight-bar-${index}`}
                  style={{ left: `${left}%`, width: `${width}%` }}
                />
              );
            })}
            <P.ProgressFill style={{ width: `${progress}%` }} />
            <P.ProgressThumb style={{ left: `${progress}%` }} />
            <P.MarkerWrapper>
              {pointMarkers.map((marker, index) => {
                const position = (marker.start / duration) * 100;
                const positionAlign =
                  position < 1 ? "start" : position > 99 ? "end" : "center";
                return (
                  <P.MarkerContainer
                    key={index}
                    position={position}
                    positionAlign={positionAlign}
                    contentAlign="center"
                    onClick={(e) => handleMarkerClick(e, marker)}
                  >
                    <P.MarkerLine type={marker.type} />
                    <P.CircleMarker type={marker.type} />
                  </P.MarkerContainer>
                );
              })}
              {highlightMarkers.map((marker, index) => {
                const startPosition = (marker.start / duration) * 100;
                const endPosition = (marker.end / duration) * 100;
                return (
                  <React.Fragment key={`highlight-markers-${index}`}>
                    <P.MarkerContainer
                      key={`highlight-start-${index}`}
                      position={startPosition}
                      positionAlign="start"
                      contentAlign="start"
                      onClick={(e) => handleMarkerClick(e, marker)}
                    >
                      <P.MarkerLine type={marker.type} />
                      <SparkleIcon color="#FF23BD" />
                    </P.MarkerContainer>
                    <P.MarkerContainer
                      key={`highlight-end-${index}`}
                      position={endPosition}
                      positionAlign="end"
                      contentAlign="end"
                      onClick={(e) => handleMarkerClick(e, marker)}
                    >
                      <P.MarkerLine type={marker.type} />
                      <SparkleIcon color="#FF23BD" />
                    </P.MarkerContainer>
                  </React.Fragment>
                );
              })}
            </P.MarkerWrapper>
          </P.TimelineContainer>
        </P.ScrollableContainer>
        <P.Controls>
          <P.PlayButton onClick={togglePlayPause}>
            {isPlaying ? "❚❚" : "▶"}
          </P.PlayButton>
          <P.TimeDisplay>
            {formatTime(currentTime)} / {formatTime(duration)}
          </P.TimeDisplay>
        </P.Controls>
      </P.TimelineControlWrapper>
      <P.UtteranceDisplay>
        {selectedMarker ? (
          <>
            <P.Timestamp>
              {formatTime(selectedMarker.start)} ~{" "}
              {formatTime(selectedMarker.end)}
            </P.Timestamp>
            {selectedMarker.speaker && (
              <SpeakerEmotionBadge
                speaker={formatSpeakerName(selectedMarker.speaker)}
                emotion={getMarkerTypeText(selectedMarker.type)}
                showIcon={false}
                showEmotion={true}
                showSpeaker={true}
              />
            )}
            <P.UtteranceText>{selectedMarker.text}</P.UtteranceText>
          </>
        ) : (
          <P.DefaultText>하이라이트를 클릭해보세요</P.DefaultText>
        )}
      </P.UtteranceDisplay>
    </P.PlayerContainer>
  );
};

export default HighlightPlayer;
